import { sleep } from '@wix/gg-components';
import { httpUtils } from '../utils/http';
import {
  suggestStoreName as suggestStoreNameHttp,
  createSitelessMetasite as createSitelessMetasiteHttp,
  attachSiteToMetasite as attachSiteToMetasiteHttp,
  installApps as installAppsHttp,
  updateSiteSettings as updateSiteSettingsHttp,
  connectPaymentProvider as connectPaymentProviderHttp,
  getPaymentAggregate as getPaymentAggregateHttp,
  getMetaSiteUrls as getMetaSiteUrlsHttp,
  initModalystStore as initModalystStoreHttp,
  setupModalystProductsByCategory as setupModalystProductsByCategoryHttp,
  initZendropStore as initZendropStoreHttp,
  setupZendropProductsByNiche as setupZendropProductsByNicheHttp,
  publishSite as publishSiteHttp,
  setupStoreCategories as setupStoreCategoriesHttp,
} from '@wix/ambassador-gg-store-setup-v1-store-setup-service/http';
import {
  SuggestStoreNameRequest,
  AttachSiteToMetasiteRequest,
  UpdateSiteSettingsRequest,
  GetPaymentAggregateResponse,
  GetMetaSiteUrlsResponse,
  InstallAppsRequest,
  SetupStoreCategoriesRequest,
  SetupZendropProductsByNicheRequest,
  StoreProductsDeletionStrategy,
} from '@wix/ambassador-gg-store-setup-v1-store-setup-service/types';
import { STORES_APP_DEF_ID } from '../config/consts';
import {
  initStore as initStoreAutodsHttp,
  setupProduct as setupProductAutodsHttp,
} from '@wix/ambassador-gg-autods-v1-autods/http';
import { SetupProductRequest as SetupProductAutodsRequest } from '@wix/ambassador-gg-autods-v1-autods/types';
import type { Category } from '../config';
import { API_ENDPOINTS, TIME_BUDGETS } from '../config/api';
import { getTemplateIdForCategory } from 'utils/templates';
import { VendorApp, vendorCategoryMap } from 'config/vendors';
import { getProductCount } from 'config/providers';
import type {
  ConnectVibePlatformHandlerRequest,
  ConnectVibePlatformHandlerResponse,
} from 'server/handlers/connectVibePlatform';
import type { CreateHeadlessSiteHandlerResponse } from 'server/handlers/createHeadlessSite';

export type CreateStoreCategoriesRequest = SetupStoreCategoriesRequest;

export async function createStoreCategories(request: CreateStoreCategoriesRequest): Promise<void> {
  await httpUtils.ambassador.request(setupStoreCategoriesHttp(request));
}

export async function createSitelessMetasite(name = 'my-site'): Promise<string> {
  const response = await httpUtils.ambassador.request(createSitelessMetasiteHttp({ name }));
  return response.data.metaSite?.metaSiteId || '';
}

export async function createHeadlessSite(): Promise<string> {
  const response = await httpUtils.client.post<CreateHeadlessSiteHandlerResponse>(
    API_ENDPOINTS.headless.createHeadlessSite,
    undefined,
    {
      timeout: TIME_BUDGETS.extended,
      headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
    },
  );

  return response.data.project?.metaSiteId || '';
}

export async function installStoresApp(): Promise<void> {
  const request: InstallAppsRequest = { apps: [{ appDefId: STORES_APP_DEF_ID }] };
  await httpUtils.ambassador.request(installAppsHttp(request));
}

export async function attachSiteToMetasite(category: Category): Promise<void> {
  const templateMetaSiteId = getTemplateIdForCategory(category);

  if (!templateMetaSiteId) {
    throw new Error(`No template ID found for category: ${category}`);
  }

  const request: AttachSiteToMetasiteRequest = { templateMetaSiteId };
  await httpUtils.ambassador.request(attachSiteToMetasiteHttp(request));
}

export async function initModalystStore(_category: Category): Promise<void> {
  await httpUtils.ambassador.request(
    initModalystStoreHttp({ deletionStrategy: StoreProductsDeletionStrategy.WAIT_FOR_PRODUCTS }),
  );
}

export async function initZendropStore(_category: Category): Promise<void> {
  await httpUtils.ambassador.request(
    initZendropStoreHttp({ deletionStrategy: StoreProductsDeletionStrategy.WAIT_FOR_PRODUCTS }),
  );
}

export async function setupModalystProducts(category: Category): Promise<void> {
  const categoryKey = vendorCategoryMap[VendorApp.Modalyst][category];
  if (!categoryKey) {
    throw new Error(`No category found for provider: ${category}`);
  }

  await httpUtils.ambassador.request(setupModalystProductsByCategoryHttp({ category: categoryKey }));
}

export async function setupZendropProducts(category: Category): Promise<void> {
  const niche = vendorCategoryMap[VendorApp.ZenDrop][category];
  if (!niche) {
    throw new Error(`No niche found for ZenDrop category: ${category}`);
  }

  await sleep(2000);

  const count = getProductCount();
  const request: SetupZendropProductsByNicheRequest = { niche, count };
  await httpUtils.ambassador.request(setupZendropProductsByNicheHttp(request));
}

export async function initAutoDSStore(_category: Category): Promise<void> {
  await httpUtils.ambassador.request(initStoreAutodsHttp({}));
}

export async function setupAutoDSProducts(category: Category): Promise<void> {
  const nicheId = vendorCategoryMap[VendorApp.AutoDs][category];
  if (!nicheId) {
    throw new Error(`No nichId found for provider: ${category}`);
  }
  const request: SetupProductAutodsRequest = { nicheId: Number(nicheId) };
  await httpUtils.ambassador.request(setupProductAutodsHttp(request));
}

export async function suggestStoreName(request: SuggestStoreNameRequest): Promise<string[]> {
  const response = await httpUtils.ambassador.request(suggestStoreNameHttp(request));
  return response.data.suggestions || [];
}

export async function getPaymentAggregators(): Promise<GetPaymentAggregateResponse> {
  const response = await httpUtils.ambassador.request(getPaymentAggregateHttp({}));
  return response.data;
}

export async function getAcceptPaymentsSetupStatus(metaSiteId: string): Promise<{ isAcceptPaymentsSetup: boolean }> {
  const response = await httpUtils.client.get<{ isAcceptPaymentsSetup: boolean }>(
    `${API_ENDPOINTS.postPurchase.acceptPaymentsSetup}?metaSiteId=${encodeURIComponent(metaSiteId)}`,
  );

  return response.data;
}

export async function connectVibePlatform(
  request: ConnectVibePlatformHandlerRequest,
): Promise<ConnectVibePlatformHandlerResponse> {
  const response = await httpUtils.client.post<ConnectVibePlatformHandlerResponse>(
    API_ENDPOINTS.headless.connectVibePlatform,
    request,
    {
      timeout: TIME_BUDGETS.extended,
      headers: { 'x-time-budget': `${TIME_BUDGETS.extended}` },
    },
  );

  return response.data;
}

export async function updateSiteSettings(siteName: string): Promise<void> {
  const request: UpdateSiteSettingsRequest = { siteDisplayName: siteName, businessName: siteName };
  await httpUtils.ambassador.request(updateSiteSettingsHttp(request));
}

export async function publishSite(): Promise<void> {
  await httpUtils.ambassador.request(publishSiteHttp({}));
}

export async function connectPaymentProvider(): Promise<void> {
  await httpUtils.ambassador.request(connectPaymentProviderHttp({}));
}

export async function getSiteUrls(): Promise<GetMetaSiteUrlsResponse> {
  const response = await httpUtils.ambassador.request(getMetaSiteUrlsHttp({}));
  return response.data;
}
